import React, { useState } from 'react';
import { Play, Calendar, ChevronLeft, ChevronRight, Film, Image as ImageIcon } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Detect if a media URL is a video by extension or common video-embed hints.
 * We consider mp4/webm/mov/m4v as inline HTML5 video. YouTube/Vimeo links are
 * treated as external — we render a "play" thumbnail that opens in new tab.
 */
export const detectMediaType = (url) => {
  if (!url) return 'image';
  const lower = url.toLowerCase();
  if (/\.(mp4|webm|mov|m4v|ogv)(\?|$)/.test(lower)) return 'video';
  if (/(youtube\.com|youtu\.be|vimeo\.com)/.test(lower)) return 'external_video';
  return 'image';
};

const resolveUrl = (raw) => {
  if (!raw) return raw;
  return raw.startsWith('http') || raw.startsWith('data:') ? raw : `${API}/files/${raw}`;
};

const MediaItem = ({ url, alt, className = '', objectClass = 'object-contain' }) => {
  const type = detectMediaType(url);
  const src = resolveUrl(url);
  if (type === 'video') {
    return (
      <video
        src={src}
        controls
        playsInline
        preload="metadata"
        className={`w-full h-full ${objectClass} bg-black ${className}`}
        data-testid="media-video"
      >
        Tu navegador no soporta video HTML5.
      </video>
    );
  }
  if (type === 'external_video') {
    return (
      <span
        role="link"
        tabIndex={0}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(src, '_blank', 'noopener,noreferrer'); }}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); window.open(src, '_blank', 'noopener,noreferrer'); } }}
        className={`relative flex items-center justify-center bg-black w-full h-full cursor-pointer ${className}`}
        data-testid="media-external-video"
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-lg">
            <Play className="w-7 h-7 text-black translate-x-0.5" fill="currentColor" />
          </div>
        </div>
        <span className="absolute bottom-2 right-2 text-[10px] text-white/80 bg-black/60 px-2 py-0.5 rounded-full">
          Ver video externo
        </span>
      </span>
    );
  }
  return (
    <img
      src={src}
      alt={alt || 'Evento'}
      className={`w-full h-full ${objectClass} ${className}`}
      loading="lazy"
      data-testid="media-image"
    />
  );
};

/**
 * EventMediaGallery
 *
 * Renders up to 3 media items (images or a video).
 * - Preserves aspect ratio: the container has a max height and `object-contain`
 *   with a soft blurred/gradient background so vertical images and horizontal
 *   images both look good without being cropped.
 * - Shows thumbnails when there is more than 1 item.
 *
 * Props:
 *  - images: string[]         (image URLs or stored keys)
 *  - videoUrl: string | null  (single primary video, HTML5-playable or external)
 *  - alt: string
 *  - variant: 'hero' | 'card' | 'thumbnail'
 */
const EventMediaGallery = ({ images = [], videoUrl = null, alt = '', variant = 'hero' }) => {
  // Assemble the media list; video (if present) leads.
  const items = [];
  if (videoUrl) items.push(videoUrl);
  (images || []).forEach((img) => {
    if (img && !items.includes(img)) items.push(img);
  });
  const sliced = items.slice(0, 3);

  const [activeIdx, setActiveIdx] = useState(0);

  // ------- Card variant: single 16/9 with object-cover, subtle video badge -------
  if (variant === 'card') {
    const first = sliced[0];
    if (!first) {
      return (
        <div className="w-full h-full flex items-center justify-center bg-[#F5F5F5]">
          <Calendar className="w-12 h-12 text-[#737373]" />
        </div>
      );
    }
    const firstType = detectMediaType(first);
    return (
      <div className="relative w-full h-full overflow-hidden bg-[#F5F5F5]">
        {firstType === 'image' ? (
          <img
            src={resolveUrl(first)}
            alt={alt}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <>
            {/* For video card thumbnails, render a still frame if HTML5 or a black bg */}
            <div className="w-full h-full bg-black">
              <MediaItem url={first} alt={alt} objectClass="object-cover" />
            </div>
          </>
        )}
        {(sliced.length > 1 || videoUrl) && (
          <div className="absolute top-2 right-2 flex gap-1">
            {videoUrl && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-black/70 text-white text-[10px] font-medium rounded-full backdrop-blur-sm">
                <Film className="w-3 h-3" /> Video
              </span>
            )}
            {sliced.length > 1 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-black/70 text-white text-[10px] font-medium rounded-full backdrop-blur-sm">
                <ImageIcon className="w-3 h-3" /> {sliced.length}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }
  // ------- Empty state -------
  if (sliced.length === 0) {
    return (
      <div className="relative w-full aspect-[16/9] bg-[#F5F5F5] rounded-lg overflow-hidden flex items-center justify-center border border-[#E5E5E5]">
        <Calendar className="w-24 h-24 text-[#737373]" />
      </div>
    );
  }

  const active = sliced[activeIdx];

  // ------- Hero variant: aspect-ratio-safe display + thumbnails -------
  return (
    <div className="space-y-3" data-testid="event-media-gallery">
      <div className="relative w-full rounded-lg overflow-hidden border border-[#E5E5E5] bg-[#0A0A0A]">
        {/* Blurred backdrop for vertical images so we don't leave black voids */}
        {detectMediaType(active) === 'image' && (
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage: `url("${resolveUrl(active)}")`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(24px) saturate(1.2)',
              transform: 'scale(1.1)',
            }}
            aria-hidden
          />
        )}
        {/* Aspect-ratio wrapper: keeps the tallest render at max 480px */}
        <div className="relative w-full flex items-center justify-center" style={{ maxHeight: '480px', minHeight: '260px' }}>
          <div className="w-full" style={{ aspectRatio: '16 / 9' }}>
            <MediaItem
              url={active}
              alt={alt}
              objectClass={detectMediaType(active) === 'external_video' ? 'object-cover' : 'object-contain'}
            />
          </div>
        </div>

        {/* Prev/next arrows on hero when >1 items */}
        {sliced.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => setActiveIdx((i) => (i - 1 + sliced.length) % sliced.length)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow z-10"
              data-testid="media-prev"
              aria-label="Anterior"
            >
              <ChevronLeft className="w-5 h-5 text-black" />
            </button>
            <button
              type="button"
              onClick={() => setActiveIdx((i) => (i + 1) % sliced.length)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/85 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow z-10"
              data-testid="media-next"
              aria-label="Siguiente"
            >
              <ChevronRight className="w-5 h-5 text-black" />
            </button>
          </>
        )}
      </div>

      {/* Thumbnails */}
      {sliced.length > 1 && (
        <div className="flex gap-2" data-testid="media-thumbnails">
          {sliced.map((url, idx) => {
            const t = detectMediaType(url);
            const isActive = idx === activeIdx;
            return (
              <button
                type="button"
                key={url}
                onClick={() => setActiveIdx(idx)}
                className={`relative w-20 h-14 rounded-md overflow-hidden border-2 transition-colors ${
                  isActive ? 'border-primary' : 'border-transparent hover:border-[#E5E5E5]'
                }`}
                data-testid={`media-thumb-${idx}`}
              >
                {t === 'image' ? (
                  <img src={resolveUrl(url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-black flex items-center justify-center">
                    <Play className="w-4 h-4 text-white" fill="currentColor" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EventMediaGallery;
